import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Component, effect, inject, OnInit, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  AbstractControl,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  ValidationErrors,
  Validators
} from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { debounceTime, distinctUntilChanged, firstValueFrom } from 'rxjs';
import { LocaleService } from '../../i18n/locale.service';
import { TranslatePipe } from '../../i18n/translate.pipe';
import { TenantService } from '../tenant.service';

type UrlValidationState = 'idle' | 'validating' | 'valid' | 'invalid';

interface WebClientConfig {
  apiBaseUrl?: string;
  security?: {
    authority?: string;
    issuer?: string;
  };
}

@Component({
  selector: 'cmi-tenants-form',
  imports: [RouterLink, ReactiveFormsModule, TranslatePipe],
  templateUrl: './form.html'
})
export class Form implements OnInit {
  private readonly _tenantService = inject(TenantService);
  private readonly _router = inject(Router);
  private readonly _route = inject(ActivatedRoute);
  private readonly _http = inject(HttpClient);
  private readonly _localeService = inject(LocaleService);
  private _originalTenantName: string | null = null;

  protected readonly urlValidationState = signal<UrlValidationState>('idle');
  protected readonly urlValidationError = signal<string>('');
  protected readonly stsValidationState = signal<UrlValidationState>('idle');
  protected readonly stsValidationError = signal<string>('');
  protected readonly webClientValidationState = signal<UrlValidationState>('idle');
  protected readonly webClientValidationError = signal<string>('');
  private _webClientRequestId = 0;

  protected readonly form = new FormGroup({
    name: new FormControl('', {
      nonNullable: true,
      validators: [
        // eslint-disable-next-line @typescript-eslint/unbound-method
        Validators.required,
        this._uniqueNameValidator.bind(this)
      ]
    }),
    webClientUrl: new FormControl('', {
      nonNullable: true
    }),
    apiUrl: new FormControl('', {
      nonNullable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      validators: [Validators.required]
    }),
    stsUrl: new FormControl('', {
      nonNullable: true,
      // eslint-disable-next-line @typescript-eslint/unbound-method
      validators: [Validators.required]
    })
  });

  private readonly _apiUrl = toSignal(
    this.form.controls.apiUrl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()),
    { initialValue: '' }
  );

  private readonly _stsUrl = toSignal(
    this.form.controls.stsUrl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()),
    { initialValue: '' }
  );

  private readonly _webClientUrl = toSignal(
    this.form.controls.webClientUrl.valueChanges.pipe(debounceTime(500), distinctUntilChanged()),
    { initialValue: '' }
  );

  constructor() {
    // Use effect to react to URL changes
    effect(() => {
      const url = this._apiUrl();
      this._validateApiUrl(this._joinUrl(url, 'api/core/ServerInfo/ServerVersion'));
    });

    effect(() => {
      const url = this._stsUrl();
      this._validateStsUrl(this._joinUrl(url, 'identity/.well-known/openid-configuration'));
    });

    effect(() => {
      const url = this._webClientUrl();
      void this._resolveFromWebClientUrl(url);
    });
  }

  public ngOnInit(): void {
    this._originalTenantName = this._route.snapshot.paramMap.get('name');
    if (this._originalTenantName) {
      const tenant = this._tenantService.getTenantByName(
        decodeURIComponent(this._originalTenantName)
      );
      if (tenant) {
        const { webClientUrl, ...rest } = tenant;
        this.form.patchValue(rest);
        // Set silently: re-emitting would re-trigger the webClientUrl auto-resolve effect,
        // which would overwrite the just-loaded (possibly manually edited) apiUrl/stsUrl
        // with a fresh config.json fetch.
        if (webClientUrl) {
          this.form.controls.webClientUrl.setValue(webClientUrl, { emitEvent: false });
        }
      }
    }
  }

  private _uniqueNameValidator(control: AbstractControl): ValidationErrors | null {
    const name = control.value as string;
    if (!name) {
      return null;
    }

    const existingTenant = this._tenantService
      .getTenants()
      .find((tenant) => tenant.name.toLowerCase() === name.toLowerCase());

    // If we're editing, allow the current tenant's name
    if (existingTenant && existingTenant.name !== this._originalTenantName) {
      return { uniqueName: true };
    }

    return null;
  }

  private _validateApiUrl(url: string): void {
    if (!url || url.trim() === '') {
      this.urlValidationState.set('idle');
      this.urlValidationError.set('');
      return;
    }

    this.urlValidationState.set('validating');
    this.urlValidationError.set('');

    this._http.get(url, { observe: 'response' }).subscribe({
      next: (response) => {
        if (response.status >= 200 && response.status < 300) {
          this.urlValidationState.set('valid');
          this.urlValidationError.set('');
        } else {
          this.urlValidationState.set('invalid');
          this.urlValidationError.set(
            this._localeService.translate('tenantForm.urlValidation.errorStatus', {
              status: response.status.toString()
            })
          );
        }
      },
      error: () => {
        this.urlValidationState.set('invalid');
        this.urlValidationError.set(
          this._localeService.translate('tenantForm.urlValidation.errorUnreachable')
        );
      }
    });
  }

  private _validateStsUrl(url: string): void {
    if (!url || url.trim() === '') {
      this.stsValidationState.set('idle');
      this.stsValidationError.set('');
      return;
    }

    this.stsValidationState.set('validating');
    this.stsValidationError.set('');

    this._http.get(url, { observe: 'response' }).subscribe({
      next: (response) => {
        if (response.status >= 200 && response.status < 300) {
          this.stsValidationState.set('valid');
          this.stsValidationError.set('');
        } else {
          this.stsValidationState.set('invalid');
          this.stsValidationError.set(
            this._localeService.translate('tenantForm.urlValidation.errorStatus', {
              status: response.status.toString()
            })
          );
        }
      },
      error: () => {
        this.stsValidationState.set('invalid');
        this.stsValidationError.set(
          this._localeService.translate('tenantForm.urlValidation.errorUnreachable')
        );
      }
    });
  }

  private async _resolveFromWebClientUrl(webClientUrl: string): Promise<void> {
    const requestId = ++this._webClientRequestId;

    if (!webClientUrl || webClientUrl.trim() === '') {
      this.webClientValidationState.set('idle');
      this.webClientValidationError.set('');
      return;
    }

    this.webClientValidationState.set('validating');
    this.webClientValidationError.set('');

    let configUrl = '';
    try {
      configUrl = this._buildConfigJsonUrl(webClientUrl);
    } catch {
      if (this._webClientRequestId !== requestId) {
        return;
      }
      this.webClientValidationState.set('invalid');
      this.webClientValidationError.set(
        this._localeService.translate('tenantForm.webClientValidation.invalidUrl')
      );
      return;
    }

    try {
      const config = await firstValueFrom(this._http.get<WebClientConfig>(configUrl));
      const apiUrl = (config.apiBaseUrl?.trim() ?? '').replace(/\/api\/?$/i, '');
      const stsUrl = (config.security?.authority ?? config.security?.issuer ?? '')
        .trim()
        .replace(/\/identity\/?$/i, '');

      if (!apiUrl || !stsUrl) {
        throw new Error('missing-config-keys');
      }

      if (this._webClientRequestId !== requestId) {
        return;
      }

      this.form.controls.apiUrl.setValue(apiUrl);
      this.form.controls.stsUrl.setValue(stsUrl);
      this.webClientValidationState.set('valid');
      this.webClientValidationError.set('');
    } catch (error) {
      if (this._webClientRequestId !== requestId) {
        return;
      }

      this.webClientValidationState.set('invalid');
      this.webClientValidationError.set(this._mapWebClientError(error));
    }
  }

  private _buildConfigJsonUrl(webClientUrl: string): string {
    const url = new URL(webClientUrl.trim());
    url.hash = '';
    url.search = '';
    const pathname = url.pathname.endsWith('/') ? url.pathname : `${url.pathname}/`;
    url.pathname = `${pathname}config.json`;
    return url.toString();
  }

  private _joinUrl(baseUrl: string, path: string): string {
    const normalizedBase = baseUrl.trim().replace(/\/+$/, '');
    const normalizedPath = path.replace(/^\/+/, '');
    return `${normalizedBase}/${normalizedPath}`;
  }

  private _mapWebClientError(error: unknown): string {
    if (error instanceof Error && error.message === 'missing-config-keys') {
      return this._localeService.translate('tenantForm.webClientValidation.invalidConfigContent');
    }

    if (error instanceof HttpErrorResponse) {
      if (error.status === 0) {
        return this._localeService.translate('tenantForm.webClientValidation.corsOrUnreachable');
      }

      return this._localeService.translate('tenantForm.webClientValidation.errorStatus', {
        status: error.status.toString()
      });
    }

    return this._localeService.translate('tenantForm.webClientValidation.unexpected');
  }

  protected onSubmit(): void {
    if (this.form.valid) {
      if (this._originalTenantName) {
        this._tenantService.updateTenant(
          decodeURIComponent(this._originalTenantName),
          this.form.getRawValue()
        );
      } else {
        this._tenantService.saveTenant(this.form.getRawValue());
      }
      void this._router.navigate(['/tenants']);
    }
  }
}
